import { CreditCard, Filter, Search, Wallet } from "lucide-react";
import { useMemo, useRef, useState, type ReactNode } from "react";
import {
  DEFAULT_CASH_FILTERS,
  filterAndSortCashTrips,
  type CashFilters,
} from "../trips/selectors";
import {
  AGEING_BUCKETS,
  TRIP_STATUSES,
  type AgeingBucket,
  type PartyOutstanding,
  type Trip,
} from "../trips/types";
import { calculateFinancials, daysSince } from "../trips/rules";
import { CompactSelect, type SelectOption } from "../shared/CompactSelect";
import { FilterPanel } from "../shared/FilterPanel";
import { formatCurrency } from "../shared/currency";
import { StatusBadge } from "../shared/StatusBadge";
import { TablePager } from "../shared/TablePager";
import { usePagedRows } from "../shared/usePagedRows";

type MoneyPageProps = {
  trips: Trip[];
  partyOutstanding: PartyOutstanding[];
};

const ageingOptions: SelectOption<CashFilters["ageingBucket"]>[] = [
  { value: "all", label: "All ages" },
  ...AGEING_BUCKETS.map((bucket) => ({
    value: bucket,
    label: `${bucket} days`,
  })),
];

const statusOptions: SelectOption<CashFilters["status"]>[] = [
  { value: "all", label: "All statuses" },
  ...TRIP_STATUSES.map((status) => ({ value: status, label: status })),
];

const sortOptions: SelectOption<CashFilters["sort"]>[] = [
  { value: "oldest", label: "Oldest first" },
  { value: "largest", label: "Largest balance" },
  { value: "party", label: "Party name" },
];

export function MoneyPage({ trips, partyOutstanding }: MoneyPageProps) {
  const [filters, setFilters] = useState<CashFilters>(DEFAULT_CASH_FILTERS);
  const [draftFilters, setDraftFilters] =
    useState<CashFilters>(DEFAULT_CASH_FILTERS);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const filterTriggerRef = useRef<HTMLButtonElement>(null);

  const filteredTrips = useMemo(
    () => filterAndSortCashTrips(trips, filters),
    [filters, trips]
  );
  const { page, pageCount, pageRows, nextPage, previousPage } =
    usePagedRows(filteredTrips);

  const allOpenTrips = useMemo(
    () =>
      trips.filter(
        (trip) =>
          trip.status !== "Cancelled" &&
          calculateFinancials(trip).outstanding > 0
      ),
    [trips]
  );
  const totalOutstanding = partyOutstanding.reduce(
    (sum, party) => sum + party.outstanding,
    0
  );
  const totalReceived = trips.reduce(
    (sum, trip) => sum + calculateFinancials(trip).amountReceived,
    0
  );
  const oldestOpenDays = allOpenTrips.reduce(
    (oldest, trip) => Math.max(oldest, daysSince(trip.date)),
    0
  );
  const partyOptions: SelectOption<CashFilters["party"]>[] = [
    { value: "all", label: "All parties" },
    ...Array.from(
      new Set(allOpenTrips.map((trip) => trip.partyName).filter(Boolean))
    )
      .sort((a, b) => a.localeCompare(b))
      .map((party) => ({ value: party, label: party })),
  ];
  const outstandingByBucket = AGEING_BUCKETS.reduce<Record<AgeingBucket, number>>(
    (acc, bucket) => {
      acc[bucket] = partyOutstanding
        .filter((party) => party.ageingBucket === bucket)
        .reduce((sum, party) => sum + party.outstanding, 0);
      return acc;
    },
    { "0-15": 0, "16-30": 0, "31-60": 0, "60+": 0 }
  );
  const activeFilterCount = [
    filters.ageingBucket !== "all",
    filters.party !== "all",
    filters.status !== "all",
    filters.sort !== "oldest",
  ].filter(Boolean).length;

  const openFilters = () => {
    setDraftFilters(filters);
    setIsFilterOpen(true);
  };

  return (
    <div className="space-y-4">
      <section className="orbit-card overflow-hidden">
        <div className="grid grid-cols-1 divide-y divide-slate-200 sm:grid-cols-3 sm:divide-x sm:divide-y-0">
          <CashMetric
            label="Open receivable"
            value={formatCurrency(totalOutstanding)}
            detail={`${allOpenTrips.length} trips need collection`}
            icon={<Wallet size={17} />}
            tone="amber"
          />
          <CashMetric
            label="Collected"
            value={formatCurrency(totalReceived)}
            detail="Recorded payment entries"
            icon={<CreditCard size={17} />}
            tone="emerald"
          />
          <CashMetric
            label="Oldest open"
            value={`${oldestOpenDays} days`}
            detail="Age of the earliest balance"
            icon={<Wallet size={17} />}
            tone="rose"
          />
        </div>
      </section>

      {allOpenTrips.length === 0 ? (
        <section className="orbit-card px-4 py-8 text-center">
          <h2 className="font-semibold text-slate-950">No cash follow-up right now</h2>
          <p className="mt-1 text-sm text-slate-500">
            Open balances will appear here as trips receive partial payments.
          </p>
        </section>
      ) : (
        <div className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(260px,0.55fr)_minmax(0,1.45fr)]">
          <section className="orbit-card p-4">
            <div className="flex items-end justify-between gap-3">
              <div>
                <h2 className="font-semibold text-slate-950">Receivable ageing</h2>
                <p className="text-xs text-slate-500">Exposure by open age</p>
              </div>
              <p className="text-sm font-bold text-amber-700">
                {formatCurrency(totalOutstanding)}
              </p>
            </div>
            <div
              className="mt-4 flex h-3 overflow-hidden rounded-full bg-slate-100"
              aria-label="Receivable ageing distribution"
            >
              {AGEING_BUCKETS.map((bucket) => (
                <span
                  key={bucket}
                  className={ageingBarTone(bucket)}
                  style={{
                    width: `${
                      totalOutstanding > 0
                        ? (outstandingByBucket[bucket] / totalOutstanding) * 100
                        : 0
                    }%`,
                  }}
                />
              ))}
            </div>
            <div className="mt-4 grid grid-cols-2 gap-x-4 gap-y-3">
              {AGEING_BUCKETS.map((bucket) => (
                <div key={bucket} className="min-w-0">
                  <div className="flex items-center gap-2 text-xs text-slate-500">
                    <span className={`h-2 w-2 rounded-full ${ageingDotTone(bucket)}`} />
                    {bucket} days
                  </div>
                  <p className="mt-1 truncate text-sm font-bold text-slate-950">
                    {formatCurrency(outstandingByBucket[bucket])}
                  </p>
                </div>
              ))}
            </div>
          </section>

          <section className="orbit-card min-w-0 overflow-hidden">
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 p-3">
              <div>
                <h2 className="font-semibold text-slate-950">Settlement Queue</h2>
                <p className="hidden text-xs text-slate-500 sm:block">
                  Trip balances ready for collection follow-up.
                </p>
              </div>
              <div className="flex min-w-0 flex-1 items-center justify-end gap-2 sm:flex-none">
                <label className="relative min-w-0 flex-1 sm:w-64">
                  <Search
                    size={16}
                    className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-500"
                  />
                  <span className="sr-only">Search settlements</span>
                  <input
                    value={filters.query}
                    onChange={(event) =>
                      setFilters((current) => ({
                        ...current,
                        query: event.target.value,
                      }))
                    }
                    placeholder="Search trip or party"
                    className="h-11 w-full rounded-xl border pl-9 pr-3 text-base outline-none sm:h-9 sm:text-sm"
                  />
                </label>
                <button
                  ref={filterTriggerRef}
                  type="button"
                  onClick={openFilters}
                  className="orbit-tab relative inline-flex h-11 shrink-0 items-center gap-2 rounded-xl border border-slate-200 px-3 text-sm font-semibold sm:h-9"
                >
                  <Filter size={16} />
                  <span className="hidden sm:inline">Filter</span>
                  {activeFilterCount > 0 ? (
                    <span className="orbit-tone-amber rounded-full px-1.5 py-0.5 text-[10px]">
                      {activeFilterCount}
                    </span>
                  ) : null}
                </button>
              </div>
            </div>

            {filteredTrips.length === 0 ? (
              <div className="px-4 py-8 text-center text-sm text-slate-500">
                No open balance matches these filters.
              </div>
            ) : (
              <>
                <div className="divide-y divide-slate-100 lg:hidden">
                  {pageRows.map((trip) => {
                    const financials = calculateFinancials(trip);
                    return (
                      <article key={trip.id} className="px-3 py-2.5">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <p className="truncate text-sm font-semibold text-slate-950">
                              {trip.tripNo || trip.vehicleNo}
                            </p>
                            <p className="truncate text-xs text-slate-500">
                              {trip.partyName || "Party not set"} · {trip.origin} - {trip.destination}
                            </p>
                          </div>
                          <p className="shrink-0 text-sm font-bold text-amber-700">
                            {formatCurrency(financials.outstanding)}
                          </p>
                        </div>
                        <div className="mt-2 flex items-center justify-between gap-3 text-xs">
                          <StatusBadge status={trip.status} />
                          <span className="text-slate-500">
                            Received {formatCurrency(financials.amountReceived)}
                          </span>
                        </div>
                      </article>
                    );
                  })}
                </div>
                <div className="hidden lg:block">
                  <table className="w-full table-fixed text-left text-sm">
                    <thead className="text-xs uppercase text-slate-500">
                      <tr>
                        <th className="w-[31%] px-3 py-2.5">Trip</th>
                        <th className="w-[20%] px-3 py-2.5">Party</th>
                        <th className="w-[17%] px-3 py-2.5">Status</th>
                        <th className="w-[16%] px-3 py-2.5 text-right">Received</th>
                        <th className="w-[16%] px-3 py-2.5 text-right">Balance</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {pageRows.map((trip) => {
                        const financials = calculateFinancials(trip);
                        return (
                          <tr key={trip.id}>
                            <td className="px-3 py-2.5">
                              <p className="truncate font-semibold text-slate-950">
                                {trip.tripNo || trip.vehicleNo}
                              </p>
                              <p className="truncate text-xs text-slate-500">
                                {trip.origin} - {trip.destination}
                              </p>
                            </td>
                            <td className="truncate px-3 py-2.5">{trip.partyName || "Not set"}</td>
                            <td className="px-3 py-2.5"><StatusBadge status={trip.status} /></td>
                            <td className="px-3 py-2.5 text-right">
                              {formatCurrency(financials.amountReceived)}
                            </td>
                            <td className="px-3 py-2.5 text-right font-bold text-amber-700">
                              {formatCurrency(financials.outstanding)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <TablePager
                  page={page}
                  pageCount={pageCount}
                  totalRows={filteredTrips.length}
                  onPrevious={previousPage}
                  onNext={nextPage}
                />
              </>
            )}
          </section>
        </div>
      )}

      <FilterPanel
        open={isFilterOpen}
        title="Cash filters"
        triggerRef={filterTriggerRef}
        onClose={() => setIsFilterOpen(false)}
        onReset={() => setDraftFilters(DEFAULT_CASH_FILTERS)}
        onApply={() => {
          setFilters((current) => ({ ...draftFilters, query: current.query }));
          setIsFilterOpen(false);
        }}
      >
        <FilterField label="Ageing">
          <CompactSelect
            ariaLabel="Cash ageing filter"
            value={draftFilters.ageingBucket}
            options={ageingOptions}
            onChange={(ageingBucket) =>
              setDraftFilters((current) => ({ ...current, ageingBucket }))
            }
          />
        </FilterField>
        <FilterField label="Party">
          <CompactSelect
            ariaLabel="Cash party filter"
            value={draftFilters.party}
            options={partyOptions}
            onChange={(party) =>
              setDraftFilters((current) => ({ ...current, party }))
            }
          />
        </FilterField>
        <FilterField label="Status">
          <CompactSelect
            ariaLabel="Cash status filter"
            value={draftFilters.status}
            options={statusOptions}
            onChange={(status) =>
              setDraftFilters((current) => ({ ...current, status }))
            }
          />
        </FilterField>
        <FilterField label="Order">
          <CompactSelect
            ariaLabel="Cash sort order"
            value={draftFilters.sort}
            options={sortOptions}
            onChange={(sort) =>
              setDraftFilters((current) => ({ ...current, sort }))
            }
          />
        </FilterField>
      </FilterPanel>
    </div>
  );
}

function CashMetric({
  label,
  value,
  detail,
  icon,
  tone,
}: {
  label: string;
  value: string;
  detail: string;
  icon: ReactNode;
  tone: "amber" | "emerald" | "rose";
}) {
  return (
    <div className="flex min-w-0 items-center gap-3 px-4 py-3">
      <span className={`orbit-tone-${tone} grid h-9 w-9 shrink-0 place-items-center rounded-lg`}>
        {icon}
      </span>
      <div className="min-w-0">
        <p className="text-[11px] font-bold uppercase text-slate-500">{label}</p>
        <p className="truncate text-lg font-bold text-slate-950">{value}</p>
        <p className="truncate text-xs text-slate-500">{detail}</p>
      </div>
    </div>
  );
}

function FilterField({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <div>
      <p className="mb-1.5 text-xs font-bold uppercase text-slate-500">{label}</p>
      {children}
    </div>
  );
}

const ageingBarTone = (bucket: AgeingBucket): string =>
  bucket === "60+"
    ? "bg-rose-500"
    : bucket === "31-60"
      ? "bg-amber-500"
      : bucket === "16-30"
        ? "bg-indigo-400"
        : "bg-emerald-500";

const ageingDotTone = (bucket: AgeingBucket): string =>
  ageingBarTone(bucket);
